import type { Filter } from "@/types/Filter";
import type { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";

/**
 * A GROUP SCOPE: "every guest of this group, across all pages".
 *
 * The column-grouped guest list shows one page of a group, and its header
 * counts the whole group. Selecting the whole group is therefore a
 * filter-wide selection — the all-matching selection the table already has,
 * NARROWED by one more row: `<group key> = <group value>`. This module builds
 * that row and decides which groups can honestly be selected that way.
 *
 * `key` is the wire key: `groupBy` already holds `additionalFields.<name>`
 * for a custom field, which is what the backend's filter parser expects.
 */
export type GroupScope = {
  key: string;
  value: string;
  /** Human label for the banner (option label / "(no value)"); not on the wire. */
  label: string;
  /** The header's count when the scope was entered — refreshed while it lives. */
  count: number;
};

/** "Is empty" — the flat-filter glyph the backend compiles to `$in [null, "", []]`. */
export const GROUP_SCOPE_EMPTY_OPERATOR = "∅";

/**
 * The one filter row the scope adds.
 *
 * Built DIRECTLY, never through `parseFiltersApiRequest`: that parser drops
 * keys it cannot resolve, coerces `("=", "")` into "is empty" only for a
 * handful of data types, and strips empty operands — any of which would
 * silently lose this row, and a lost row does not narrow the action, it WIDENS
 * it to the whole filter. The empty group is therefore spelled with the glyph
 * the backend understands for every type, enums included, and that is exactly
 * the bucket the header count folded `null`/missing/`""` into.
 */
export function buildGroupScopeFilter(scope: Pick<GroupScope, "key" | "value">): Filter {
  return scope.value === ""
    ? { key: scope.key, operator: GROUP_SCOPE_EMPTY_OPERATOR, value: "" }
    : { key: scope.key, operator: "=", value: scope.value };
}

/**
 * Narrow a parsed filter list to the scope.
 *
 * Rows on the SAME key are removed first, not merely joined: the backend
 * OR-combines user rows that share a key (`FilterParser.java`, same-key
 * `$or`), so `company ≠ Beta` beside `company = Acme` would stop narrowing.
 * Removing them is exact — every member of the group already satisfied those
 * rows, and the group key is a constant for all of them.
 */
export function applyGroupScope(
  filters: Filter[],
  scope: Pick<GroupScope, "key" | "value">,
): Filter[] {
  return [...filters.filter((f) => f.key !== scope.key), buildGroupScopeFilter(scope)];
}

/**
 * Can a bulk action select this group EXACTLY?
 *
 * False where the header's count and the filter's match would disagree, so
 * the "select all N" affordance is never offered on a number the action
 * cannot honour:
 *  - a MULTI_SELECT column: the header groups on the joined `"a, b"` string,
 *    which `$eq` never matches against the stored array — the action would
 *    touch nobody;
 *  - `checkedIn` = "false": the backend widens `= false` on this tri-state
 *    field to `$in [false, null]`, so the "false" bucket would also take the
 *    never-scanned bucket with it;
 *  - `archived`: the payload hook strips `archived` rows before parsing and
 *    sends the flag as its own parameter; a scope row on it would put a second
 *    `archived` condition into bodies the sync endpoints do not sanitise.
 */
export function isGroupScopeSupported(
  groupBy: string,
  value: string,
  field?: (Pick<Field, "dataType"> & { additionalField?: boolean }) | null,
): boolean {
  if (field?.dataType === DataType.MULTI_SELECT) return false;
  // Every checkedIn group, not only "false": the check-in, check-out and
  // reset lanes append their OWN `checkedIn` row to the user filters
  // (`GuestService.batchCheckInOut` / `batchResetCheckIn`), and same-key user
  // rows are OR-combined — `checkedIn = true` OR `checkedIn ≠ true` is
  // everyone. A scope on this column would widen exactly the actions it is
  // most likely to be used for.
  if (groupBy === "checkedIn") return false;
  if (groupBy === "archived") return false;
  // The backend trims the operand before comparing; a group keyed on a
  // stored value with edge whitespace would act on the TRIMMED sibling group
  // — different rows, not fewer.
  if (hasEdgeWhitespace(value)) return false;
  // Custom fields: the backend sniffs the operand's SHAPE (`FilterParser.
  // convertValueToAppropriateType`) — a text value that looks like a number,
  // a boolean or a date is compared as one, and never equals the stored
  // string. Only a field whose type IS that shape round-trips.
  if (field?.additionalField && looksTyped(value) && !TYPED_CUSTOM_FIELDS.has(field.dataType)) {
    return false;
  }
  return true;
}

const TYPED_CUSTOM_FIELDS: ReadonlySet<DataType> = new Set([
  DataType.BOOLEAN,
  DataType.LONG,
  DataType.DOUBLE,
  DataType.CURRENCY,
  DataType.DATE,
  DataType.DATE_TIME,
]);

/**
 * Edge whitespace as the backend's `FieldValueNormalizer` sees it: JS `trim`
 * plus the zero-width space and the BOM, which its edge class strips too.
 */
function hasEdgeWhitespace(value: string): boolean {
  return /^[\s​﻿]|[\s​﻿]$/.test(value);
}

/** Would the backend's operand sniffing turn this string into a non-string? */
function looksTyped(value: string): boolean {
  // `isBooleanString` compares case-insensitively — "TRUE" is what an Excel
  // export writes.
  if (/^(true|false)$/i.test(value)) return true;
  if (/^-?\d+(\.\d+)?$/.test(value)) return true;
  // ISO-8601 date or instant.
  if (/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/.test(value)) return true;
  return false;
}
